import clsx from "classnames";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { INDIAN_STATES } from "../../../constants/state";
import {
  TypographyColor,
  TypographyVariant,
} from "../../../constants/typography";
import { useGamepadContext } from "../../../contexts/GamepadProvider";
import { Dialog } from "../../core/Dialog/Dialog";
import { Icon } from "../../core/Icon/Icon";
import { TextField } from "../../core/TextField/TextField";
import { Typography } from "../../core/Typography/Typography";
import StateDropDown from "../StateDropDown/StateDropDown";
import { useStyles } from "./BillingInfoPopup.style";
import { useQuery } from "@apollo/client";
import { meAAAGQL, Query } from "@onmo/graphql/graphql";

type BillingInfoPopupProps = {
  open: boolean;
  onClose?: () => void;
  onContinue?: (data: { name: string; state: string }) => void | Promise<void>;
};

const BillingInfoPopup: React.FC<BillingInfoPopupProps> = ({
  open,
  onClose,
  onContinue,
}) => {
  const classes = useStyles();
  const { data: meData } = useQuery<{ me: Query["me"] }>(meAAAGQL);
  const billingInfo = meData?.me?.metadata?.billingInfo;
  const [name, setName] = useState(billingInfo?.name ?? "");
  const [state, setState] = useState(billingInfo?.state ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const { setActiveElement } = useGamepadContext();
  const [triggerRect, setTriggerRect] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(billingInfo?.name ?? "");
    setState(billingInfo?.state ?? "");
  }, [open, billingInfo?.name, billingInfo?.state]);

  useLayoutEffect(() => {
    if (!isDropdownOpen) return;
    const update = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setTriggerRect({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    };
    update();
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
    };
  }, [isDropdownOpen]);

  const isValid = !!name.trim() && !!state;

  const selectedLabel = INDIAN_STATES.find((s) => s.value === state)?.label;

  const handleContinue = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    try {
      await onContinue?.({ name: name.trim(), state });
    } finally {
      setSubmitting(false);
    }
  };

  const onCloseInternal = () => {
    if (submitting) return;
    setName(billingInfo?.name ?? "");
    setState(billingInfo?.state ?? "");
    setIsDropdownOpen(false);
    onClose?.();
  };

  const onChangeName = (name: string) => {
    setName(name);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  const onSelectState = (option: { value: string }) => {
    setState(option.value);
    setIsDropdownOpen(false);
    if (triggerRef.current) setActiveElement(triggerRef.current);
  };

  useEffect(() => {
    if (!open || !isDropdownOpen || !triggerRect) return;
    const idx = Math.max(
      0,
      INDIAN_STATES.findIndex((s) => s.value === state),
    );
    const opt = document.querySelector<HTMLElement>(
      `[data-gamepad="state-${idx}"]`,
    );
    if (opt) {
      opt.setAttribute("data-gamepad-no-auto-scroll", "true");
      setActiveElement(opt);
      opt.removeAttribute("data-gamepad-no-auto-scroll");
      opt.scrollIntoView?.({ block: "nearest", behavior: "instant" });
    }
  }, [isDropdownOpen, open, state, setActiveElement, triggerRect]);

  return (
    <Dialog
      open={open}
      onClose={onCloseInternal}
      className={classes.dialog}
      mainAction={{
        text: "Continue",
        onClick: handleContinue,
        disabled: !isValid || submitting,
        loading: submitting,
        disableGamepadAction: isDropdownOpen,
      }}
    >
      <div className={classes.header}>
        <Typography
          variant={TypographyVariant.HeadingH3Bold}
          color={TypographyColor.Default}
        >
          Enter Your Details
        </Typography>
        <Typography
          variant={TypographyVariant.BodyB3Medium}
          color={TypographyColor.Default}
        >
          Before payment, please enter your name and state for your payment
          receipt.
        </Typography>
      </div>

      <div className={classes.form}>
        <div className={classes.field}>
          <Typography
            variant={TypographyVariant.BodyB5Regular}
            color={TypographyColor.Silver}
            className={classes.label}
          >
            Name
          </Typography>
          <TextField
            fullWidth
            inputProps={{
              placeholder: "Enter Your Name",
              value: name,
              dataGamepad: isDropdownOpen ? undefined : "billing-name",
            }}
            onChangeValue={(value) => onChangeName(value)}
            className={classes.input}
          />
        </div>

        <div className={classes.field}>
          <Typography
            variant={TypographyVariant.BodyB5Regular}
            color={TypographyColor.Silver}
            className={classes.label}
          >
            State
          </Typography>
          <div
            ref={triggerRef}
            className={classes.selectControl}
            onClick={toggleDropdown}
            tabIndex={0}
            data-gamepad={isDropdownOpen ? undefined : "state-select"}
            data-selected-class={classes.selectControlActive}
          >
            <Typography
              variant={TypographyVariant.BodyB5Regular}
              color={
                selectedLabel ? TypographyColor.Default : TypographyColor.Gray
              }
              className={classes.label}
            >
              {selectedLabel || "Choose Your State"}
            </Typography>
            <Icon
              name="icon-arrow-down"
              size={16}
              className={clsx(classes.selectIcon, {
                [classes.selectIconOpen]: isDropdownOpen,
              })}
            />
          </div>
          {isDropdownOpen && triggerRect && (
            <StateDropDown
              value={state}
              onSelectState={onSelectState}
              className={classes.dropdown}
              style={{
                top: triggerRect.top,
                left: triggerRect.left,
                width: triggerRect.width,
              }}
            />
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default BillingInfoPopup;
